import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Button, DatePicker, Flex, Input, TableProps, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";

import api from "@/apis/order";
import { CommonStateEnum } from "@/types/common";
import { RequirementStatusEnum } from "@/types/requirement";
import { PageId, PageType } from "@/types/page";
import { SaleOrderItemState } from "@/types/sales";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import { AnyObject } from "antd/lib/_util/type";
import useOrderColumn from "@/pages/Sales/pages/Order/hooks/useOrderColumn";
import SelectCustomer from "@/components/Selector/SelectCustomer";

const { RangePicker } = DatePicker;

interface ApprovalListProps {
  state: CommonStateEnum;
  params?: {
    body: { customerId: string; keyword: string; requireStatus?: RequirementStatusEnum };
    pagination: { offset: number; take: number };
    startTime?: number;
    endTime?: number;
  };
  requirement?: CommonStateEnum;
  orgId?: string;
}

interface SaleOfficialOrderSelectProps {
  value: SaleOrderItemState | null;
  onChange: (order: SaleOrderItemState | null) => void;
}

const SaleOfficialOrderSelect: FC<SaleOfficialOrderSelectProps> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const tableRef = useRef<TableLayoutRef | null>(null);

  const [list, total, listLoading, { fetchList }] = useDataList<SaleOrderItemState, [ApprovalListProps]>({
    fetchApi: api.approvalOrderList,
  });
  const [keyword, setKeyword] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  // 直接复用销售正式订单列表列配置，只通过 override 切到正式订单页面语义。
  const baseColumns = useOrderColumn({
    onDetail: () => { },
    onDel: () => { },
    onOpenTab: () => { },
    onOpenFinanceRequire: () => { },
    onOut: () => { },
    onOpenRequirement: () => { },
    openSwap: () => { },
    pageIdOverride: PageId.SALE_OFFICIAL,
    pageTypeOverride: PageType.SALE,
  });

  const columns = useMemo(() => {
    // 这里只负责“选订单”，所以去掉关联单据和操作列。
    return baseColumns.filter(column => {
      const dataIndex = "dataIndex" in column
        ? (Array.isArray(column.dataIndex) ? column.dataIndex.join(".") : column.dataIndex)
        : undefined;
      return dataIndex !== "relatedDocuments" && dataIndex !== "operation";
    });
  }, [baseColumns]);

  const fetch = (offset: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    fetchList({
      state: CommonStateEnum.ACTIVE,
      params: {
        body: { customerId, keyword, requireStatus: undefined },
        pagination: { offset, take },
        startTime: startTime ? startTime.startOf("day").valueOf() : undefined,
        endTime: endTime ? endTime.endOf("day").valueOf() : undefined,
      },
    }).catch(() => { });
  };

  useEffect(() => {
    fetch(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const headerChildren = (
    <Flex gap={10} justify="end" wrap="wrap" style={{ width: "100%" }}>
      <SelectCustomer onChange={setCustomerId} allowClear style={{ width: 220 }} />
      <RangePicker
        size="small"
        onChange={val => {
          if (val) {
            setStartTime(val[0]);
            setEndTime(val[1]);
          } else {
            setStartTime(null);
            setEndTime(null);
          }
        }}
      />
      <Tooltip title={t("sale.orderSearchTip")}>
        <Input
          size="small"
          placeholder={t("sale.orderSearchTip")}
          style={{ width: 220 }}
          value={keyword}
          onChange={e => setKeyword(e.target.value.trim())}
        />
      </Tooltip>
      <Button
        type="primary"
        icon={<SearchOutlined />}
        onClick={() => {
          fetch(0);
          tableRef.current?.resetTablePage();
        }}
      />
    </Flex>
  );

  const rowSelection: TableProps<AnyObject>["rowSelection"] = {
    type: "radio",
    selectedRowKeys: value ? [value.id] : [],
    onChange: (_, selectedRows) => {
      onChange((selectedRows[0] as SaleOrderItemState) || null);
    },
  };

  return (
    <STableLayout
      rowKey="id"
      loading={listLoading}
      dataSource={list}
      columns={columns as TableProps<AnyObject>["columns"]}
      headerChildren={headerChildren}
      tableLayoutRef={tableRef}
      total={total}
      handleChange={offset => fetch(offset)}
      rowSelection={rowSelection}
    />
  );
};

export default SaleOfficialOrderSelect;
